/**
 * "Avaliações e promoções" on a rider record (D580): the ratings the rider received from drivers and gave to them, the promotions it
 * redeemed and the discount they added up to (both from the analytics summary), and — only when the rider has any — its loyalty
 * accounts and subscriptions. The loyalty block is read on its own and stays out of the card when there is nothing to show.
 */
import { type ReactElement } from 'react';
import { type CustomerSummary, type DirectoryUser } from '@vello/api-client';
import { Badge, Ink, KeyValueList, RecordRow, RecordSection, Text } from '@vello/web-theme';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { CardBody, PersonCard, useCardRead, type CardState } from './person-card';
import { usePersonFormat } from './person-format';

export function PersonRatingsCard({ user, summary, reload }: { user: DirectoryUser; summary: CardState<CustomerSummary> | undefined; reload: () => void }): ReactElement {
  const { runtime } = useAdmin();
  const { t } = useT();
  const { v } = useVocab();
  const fmt = usePersonFormat();
  const loyalty = useCardRead(
    async () => {
      const [accounts, subscriptions] = await Promise.all([runtime.riderLoyalty.listAccounts(user.id), runtime.riderLoyalty.listSubscriptions(user.id)]);
      if (!accounts.ok) return accounts;
      if (!subscriptions.ok) return subscriptions;
      return { ok: true as const, value: { accounts: accounts.value.accounts, subscriptions: subscriptions.value.subscriptions } };
    },
    [runtime, user.id],
  );
  const hideLoyalty = loyalty.state?.status === 'ready' && loyalty.state.value.accounts.length === 0 && loyalty.state.value.subscriptions.length === 0;

  const ratingText = (r: { readonly count: number; readonly averageHundredths: number | null }): string =>
    r.count === 0 || r.averageHundredths === null ? t('prec.ratings.none') : t('prec.ratings.value', { average: fmt.rating(r.averageHundredths), count: fmt.count(r.count) });

  return (
    <PersonCard title={t('prec.ratings.title')} what={t('prec.what.ratings')} state={summary} reload={reload} heldBack={t('prec.needsMarket')}>
      {(s) => (
        <>
          <KeyValueList
            dense
            items={[
              { label: t('prec.ratings.received'), value: ratingText(s.ratingsReceived), mono: true },
              { label: t('prec.ratings.given'), value: ratingText(s.ratingsGiven), mono: true },
              { label: t('prec.promos.redemptions'), value: fmt.count(s.promotions.redemptions), mono: true },
              { label: t('prec.promos.discount'), value: fmt.money(s.promotions.discount), mono: true },
            ]}
          />
          {!hideLoyalty && (
            <RecordSection>
              <Text as="h3" role="label">
                {t('prec.loyalty.title')}
              </Text>
              <CardBody what={t('prec.what.loyalty')} state={loyalty.state} reload={loyalty.reload}>
                {(l) => (
                  <>
                    {l.accounts.map((a) => (
                      <RecordRow key={a.accountId} dense>
                        <Ink strong>{a.tier}</Ink>
                        <Text role="caption" tone="secondary">
                          {t('prec.loyalty.points', { points: fmt.count(a.pointsBalance) })}
                        </Text>
                        <span className="vello-grow" />
                        <Ink mono>{fmt.when(a.updatedAt)}</Ink>
                      </RecordRow>
                    ))}
                    {l.subscriptions.map((sub) => (
                      <RecordRow key={sub.subscriptionId} dense>
                        <Badge tone={sub.state === 'ACTIVE' ? 'success' : 'neutral'}>{v('rider_loyalty.subscription_state', sub.state)}</Badge>
                        <Text role="caption" tone="secondary">
                          {t('prec.loyalty.plan', { price: fmt.money({ amountMinor: sub.plan.priceMinor, currency: sub.plan.currency }), days: String(sub.plan.periodDays) })}
                        </Text>
                        <span className="vello-grow" />
                        <Ink mono>{t('prec.loyalty.renews', { when: fmt.day(sub.currentPeriodEnd) })}</Ink>
                      </RecordRow>
                    ))}
                  </>
                )}
              </CardBody>
            </RecordSection>
          )}
        </>
      )}
    </PersonCard>
  );
}
